// Production build - error handling improved
// Force rebuild for production API
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useConfig } from '../context/ConfigContext';
import TaxInstallmentLines from '../components/TaxInstallmentLines';

function PropertyManagement() {
  const { isDemoMode } = useConfig();
  const demoDisabled = isDemoMode();
  const demoStyle = demoDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : {};
  const navigate = useNavigate();
  const { propertyId } = useParams();
  const editFormRef = useRef(null);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [showExpensesModal, setShowExpensesModal] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [showAddExpenseForm, setShowAddExpenseForm] = useState(false);
  const [expenseFormData, setExpenseFormData] = useState({
    expense_date: new Date().toISOString().split('T')[0],
    category: 'Postal/Mailing',
    description: '',
    amount: ''
  });
  const [showImagesModal, setShowImagesModal] = useState(false);
  const [selectedPropertyForImages, setSelectedPropertyForImages] = useState(null);
  const [images, setImages] = useState([]);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [imageCaption, setImageCaption] = useState('');
  const [loadingImages, setLoadingImages] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [draggedImageIndex, setDraggedImageIndex] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const [showTaxPaymentModal, setShowTaxPaymentModal] = useState(false);
  const [selectedPropertyForTax, setSelectedPropertyForTax] = useState(null);
  const [taxPayments, setTaxPayments] = useState([]);
  const [showAddTaxPaymentForm, setShowAddTaxPaymentForm] = useState(false);
  const [taxPaymentFormData, setTaxPaymentFormData] = useState({
    payment_date: new Date().toISOString().split('T')[0],
    amount: '',
    tax_year: new Date().getFullYear(),
    payment_method: 'Check',
    check_number: '',
    instalment: '',
    notes: ''
  });
  // The county schedule for this property's loan, and what has been remitted
  // against it. Supplied by the server, which decides every status on it.
  const [taxSchedule, setTaxSchedule] = useState(null);

  const loadProperties = useCallback(async () => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        navigate('/admin/login');
        return;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/properties`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setProperties(response.data);
      setLoading(false);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        navigate('/admin/login');
      } else {
        setLoading(false);
      }
    }
  }, [navigate]);

  useEffect(() => {
    loadProperties();
  }, [loadProperties]);

  const loadExpenses = async (propertyId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/properties/${propertyId}/expenses`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setExpenses(response.data);
    } catch (err) {
      console.error('Failed to load expenses:', err);
      alert('Failed to load expenses');
    }
  };

  const openExpensesModal = (property) => {
    setSelectedProperty(property);
    setShowExpensesModal(true);
    loadExpenses(property.id);
  };

  const handleAddExpense = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/admin/properties/${selectedProperty.id}/expenses`,
        expenseFormData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Expense added successfully!');
      setShowAddExpenseForm(false);
      setExpenseFormData({
        expense_date: new Date().toISOString().split('T')[0],
        category: 'Postal/Mailing',
        description: '',
        amount: ''
      });
      loadExpenses(selectedProperty.id);
      loadProperties();
    } catch (err) {
      console.error('Failed to add expense:', err);
      alert('Failed to add expense');
    }
  };

  const deleteExpense = async (expenseId) => {
    if (!window.confirm('Delete this expense?')) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/admin/expenses/${expenseId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Expense deleted successfully!');
      loadExpenses(selectedProperty.id);
      loadProperties();
    } catch (err) {
      console.error('Failed to delete expense:', err);
      alert('Failed to delete expense');
    }
  };

  const loadImages = async (propertyId) => {
    setLoadingImages(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/properties/${propertyId}/images`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setImages(response.data);
    } catch (err) {
      console.error('Failed to load images:', err);
      alert('Failed to load images');
    } finally {
      setLoadingImages(false);
    }
  };

  const openImagesModal = (property) => {
    setSelectedPropertyForImages(property);
    setShowImagesModal(true);
    loadImages(property.id);
  };

  const handleImageUpload = async (e) => {
    e.preventDefault();
    
    if (!selectedImageFile) {
      alert('Please select an image file');
      return;
    }

    setUploadingImage(true);
    try {
      const token = localStorage.getItem('adminToken');
      const formData = new FormData();
      formData.append('image', selectedImageFile);
      formData.append('caption', imageCaption);

      await axios.post(
        `${process.env.REACT_APP_API_URL}/admin/properties/${selectedPropertyForImages.id}/images/upload`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );
      alert('Image uploaded successfully!');
      setSelectedImageFile(null);
      setImageCaption('');
      loadImages(selectedPropertyForImages.id);
    } catch (err) {
      console.error('Failed to upload image:', err);
      alert(err.response?.data?.error || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleFilesDrop = (e) => {
    e.preventDefault();
    setIsDraggingFiles(false);
    
    const files = Array.from(e.dataTransfer.files);
    handleFilesSelected(files);
  };

  const handleFilesSelected = (files) => {
    const imageFiles = files.filter(file => 
      file.type.startsWith('image/') && file.size <= 10 * 1024 * 1024
    );
    
    if (imageFiles.length === 0) {
      alert('Please select valid image files (JPG, PNG, GIF, WebP) under 10MB each');
      return;
    }
    
    const currentCount = images.length;
    const newFilesCount = imageFiles.length;
    const totalCount = currentCount + newFilesCount + selectedFiles.length;
    
    if (totalCount > 10) {
      alert(`Cannot add ${newFilesCount} files. Maximum 10 images per property. Currently: ${currentCount} uploaded, ${selectedFiles.length} pending.`);
      return;
    }
    
    setSelectedFiles(prev => [...prev, ...imageFiles]);
  };

  const removeSelectedFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadAllFiles = async () => {
    if (selectedFiles.length === 0) return;
    
    setUploadingImage(true);
    const progress = {};
    selectedFiles.forEach((_, index) => {
      progress[index] = { status: 'pending', percent: 0 };
    });
    setUploadProgress(progress);

    for (let i = 0; i < selectedFiles.length; i++) {
      try {
        setUploadProgress(prev => ({
          ...prev,
          [i]: { status: 'uploading', percent: 50 }
        }));

        const token = localStorage.getItem('adminToken');
        const formData = new FormData();
        formData.append('image', selectedFiles[i]);
        formData.append('caption', '');

        await axios.post(
          `${process.env.REACT_APP_API_URL}/admin/properties/${selectedPropertyForImages.id}/images/upload`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );

        setUploadProgress(prev => ({
          ...prev,
          [i]: { status: 'completed', percent: 100 }
        }));
      } catch (err) {
        console.error(`Failed to upload file ${i}:`, err);
        setUploadProgress(prev => ({
          ...prev,
          [i]: { status: 'error', percent: 0 }
        }));
      }
    }

    setTimeout(() => {
      setSelectedFiles([]);
      setUploadProgress({});
      setUploadingImage(false);
      loadImages(selectedPropertyForImages.id);
      alert('Bulk upload completed!');
    }, 500);
  };

  const updateImageCaption = async (imageId, caption) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/admin/properties/${selectedPropertyForImages.id}/images/${imageId}`,
        { caption },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Caption updated!');
      loadImages(selectedPropertyForImages.id);
    } catch (err) {
      console.error('Failed to update caption:', err);
      alert('Failed to update caption');
    }
  };

  const setFeaturedImage = async (imageId) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/admin/properties/${selectedPropertyForImages.id}/images/${imageId}`,
        { is_featured: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Featured image set!');
      loadImages(selectedPropertyForImages.id);
    } catch (err) {
      console.error('Failed to set featured image:', err);
      alert('Failed to set featured image');
    }
  };

  const deleteImage = async (imageId) => {
    if (!window.confirm('Delete this image? This will permanently remove it from Cloudinary.')) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/admin/properties/${selectedPropertyForImages.id}/images/${imageId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Image deleted successfully!');
      loadImages(selectedPropertyForImages.id);
    } catch (err) {
      console.error('Failed to delete image:', err);
      alert('Failed to delete image');
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedImageIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedImageIndex === null || draggedImageIndex === dropIndex) {
      setDraggedImageIndex(null);
      return;
    }

    const reorderedImages = [...images];
    const [draggedImage] = reorderedImages.splice(draggedImageIndex, 1);
    reorderedImages.splice(dropIndex, 0, draggedImage);

    const imageOrders = reorderedImages.map((img, idx) => ({
      id: img.id,
      display_order: idx
    }));

    setImages(reorderedImages);
    setDraggedImageIndex(null);

    try {
      const token = localStorage.getItem('adminToken');
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/admin/properties/${selectedPropertyForImages.id}/images/reorder`,
        { imageOrders },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Image order saved successfully!');
    } catch (err) {
      console.error('Failed to reorder images:', err);
      alert('Failed to save new image order');
      loadImages(selectedPropertyForImages.id);
    }
  };

  const handleDragEnd = () => {
    setDraggedImageIndex(null);
  };

const loadTaxPayments = async (propertyId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/properties/${propertyId}/escrow-disbursements`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTaxPayments(response.data);
    } catch (err) {
      console.error('Failed to load tax payments:', err);
      alert('Failed to load tax payments');
    }
  };

  const loadTaxSchedule = async (propertyId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/properties/${propertyId}/tax-schedule`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTaxSchedule(response.data);
    } catch {
      // A schedule that cannot be loaded costs the admin a prefill, not the
      // ability to record a payment. The form stays fully usable and simply
      // shows no installment lines.
      setTaxSchedule(null);
    }
  };

  // Prefill from the property's own schedule, and ONLY when the dropdown
  // changes. The amount stays editable afterwards: nothing here runs again to
  // undo what the admin typed, which is why this is a handler and not an effect.
  const applyInstalmentSelection = (value) => {
    setTaxPaymentFormData((prev) => {
      const next = { ...prev, instalment: value };
      if (!taxSchedule) return next;

      if (value === '') {
        if (taxSchedule.annual_tax_amount > 0) {
          next.amount = taxSchedule.annual_tax_amount.toFixed(2);
        }
        return next;
      }

      const chosen = (taxSchedule.instalments || [])
        .find((row) => String(row.instalment) === String(value));
      if (!chosen) return next;

      if (chosen.amount > 0) next.amount = chosen.amount.toFixed(2);
      // The tax year follows the selected installment's OWN due date, so a 2nd
      // installment due in September 2026 defaults to 2026 and a 1st
      // installment due in February 2027 defaults to 2027, rather than to
      // whatever calendar year the admin happens to be sitting in.
      if (chosen.tax_year) next.tax_year = chosen.tax_year;
      return next;
    });
  };

  const openTaxPaymentModal = useCallback((property) => {
    setSelectedPropertyForTax(property);
    setShowTaxPaymentModal(true);
    setTaxSchedule(null);
    loadTaxPayments(property.id);
    loadTaxSchedule(property.id);
  }, []);

  // Auto-open tax payment modal if propertyId in URL
  useEffect(() => {
    if (propertyId && properties.length > 0 && !loading) {
      const property = properties.find(p => p.id === parseInt(propertyId));
      if (property) {
        openTaxPaymentModal(property);
        // Clear the URL to avoid re-opening on refresh
        navigate('/admin/properties', { replace: true });
      }
    }
  }, [propertyId, properties, loading, navigate, openTaxPaymentModal]);

  const handleAddTaxPayment = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('adminToken');
      // The escrow ledger is keyed to a LOAN, not a property. The server
      // resolves the property's active loan when loan_id is left blank, and
      // refuses rather than guessing when the property carries more than one.
      await axios.post(
        `${process.env.REACT_APP_API_URL}/admin/escrow-disbursements`,
        {
          ...taxPaymentFormData,
          property_id: selectedPropertyForTax.id,
          kind: 'tax',
          // Blank is "not tied to one instalment". Sent as null so the server
          // stores a real NULL rather than an empty string.
          instalment: taxPaymentFormData.instalment === '' ? null : taxPaymentFormData.instalment
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Tax payment recorded successfully!');
      setShowAddTaxPaymentForm(false);
      setTaxPaymentFormData({
        payment_date: new Date().toISOString().split('T')[0],
        amount: '',
        tax_year: new Date().getFullYear(),
        payment_method: 'Check',
        check_number: '',
        instalment: '',
        notes: ''
      });
      loadTaxPayments(selectedPropertyForTax.id);
      loadTaxSchedule(selectedPropertyForTax.id);
      loadProperties();
    } catch (err) {
      console.error('Failed to record tax payment:', err);
      alert('Failed to record tax payment');
    }
  };

  const deleteTaxPayment = async (paymentId) => {
    if (!window.confirm('Delete this tax payment?')) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/admin/escrow-disbursements/${paymentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Tax payment deleted successfully!');
      loadTaxPayments(selectedPropertyForTax.id);
      loadTaxSchedule(selectedPropertyForTax.id);
      loadProperties();
    } catch (err) {
      console.error('Failed to delete tax payment:', err);
      alert('Failed to delete tax payment');
    }
  };

  const updateStatus = async (propertyId, newStatus) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/admin/properties/${propertyId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadProperties();
    } catch (err) {
      console.error('Update status error:', err);

      // A blocked publish is not a failure to save -- it is a setup step the
      // tenant has not done yet, and the server sends back exactly which one and
      // the page that fixes it. Prefixing this with "Failed to update status"
      // would bury the only sentence that helps.
      const data = err.response?.data;
      if (data?.code === 'FINANCING_TIERS_NOT_CONFIGURED') {
        if (window.confirm(`${data.error}\n\nOpen ${data.fixLabel} now?`)) {
          navigate(data.fixPath);
        }
        loadProperties(); // put the dropdown back to the real stored status
        return;
      }

      alert('Failed to update status: ' + (data?.error || err.message));
    }
  };

  const deleteProperty = async (propertyId, propertyTitle) => {
    if (!window.confirm(`Are you sure you want to DELETE "${propertyTitle}"?\n\nThis action cannot be undone!`)) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/admin/properties/${propertyId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Property deleted successfully!');
      loadProperties();
    } catch (err) {
      console.error('Delete property error:', err);
      alert('Failed to delete property: ' + (err.response?.data?.error || err.message));
    }
  };

  const getStatusBadge = (status) => {
    // Normalize status: convert to lowercase and replace spaces with underscores
    const normalizedStatus = status ? status.toLowerCase().replace(/ /g, '_') : 'available';

    const styles = {
      // Draft is the only status not visible to buyers, so it reads as an
      // unfinished note rather than a state in the sale.
      draft: { backgroundColor: 'var(--color-surface-sunken)', color: 'var(--color-text)', border: '1px dashed var(--color-border)' },
      available: { backgroundColor: '#10b981', color: 'white' },
      pending: { backgroundColor: '#f59e0b', color: 'white' },
      under_contract: { backgroundColor: '#3b82f6', color: 'white' },
      sold: { backgroundColor: '#6b7280', color: 'white' },
      coming_soon: { backgroundColor: '#8b5cf6', color: 'white' }
    };

    const labels = {
      draft: 'DRAFT',
      available: 'AVAILABLE',
      pending: 'PENDING',
      under_contract: 'CONTRACT',
      sold: 'SOLD',
      coming_soon: 'COMING SOON'
    };

    return (
      <span style={{
        ...styles[normalizedStatus],
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '14px',
        fontWeight: '500',
        whiteSpace: 'nowrap',
        display: 'inline-block'
      }}>
        {labels[normalizedStatus] || status.toUpperCase()}
      </span>
    );
  };

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center' }}>Loading properties...</div>;
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>
      {/* Header */}
      <div style={{ marginBottom: '30px' }}>
        <h1 className="admin-page-title" style={{ margin: '0 0 5px 0' }}>🏘️ Property Management</h1>
        <p style={{ margin: '0 0 20px 0', color: 'var(--color-text-light)', textAlign: 'center' }}>
          {properties.length} total properties
        </p>
        <button
          onClick={() => !demoDisabled && setShowAddForm(!showAddForm)}
          className="btn btn-primary"
          style={{ width: '100%', marginBottom: '10px', ...demoStyle }}
          disabled={demoDisabled}
          title={demoDisabled ? 'Disabled in demo mode' : ''}
        >
          {showAddForm ? 'Cancel' : '+ Add Property'}
        </button>
        <button onClick={() => navigate('/admin/properties-hub')} className="admin-hub-back-btn">
          <span className="admin-hub-back-arrow">←</span>
          Back to Properties
        </button>
      </div>

      {/* Profit Summary Cards */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
        gap: '20px', 
        marginBottom: '30px' 
      }}>
        {/* Total Profit Card */}
        <div className="card" style={{ padding: '20px' }}>
          <h3 style={{ margin: '0 0 10px 0', fontSize: '16px', color: 'var(--color-text-light)' }}>💰 Total Profit</h3>
          <p style={{ margin: 0, fontSize: '28px', fontWeight: 'bold', color: 'var(--color-brand-text)' }}>
            ${properties
              .filter(p => p.acquisition_cost != null && parseFloat(p.acquisition_cost) > 0)
              .reduce((sum, p) => sum + (parseFloat(p.price) - parseFloat(p.acquisition_cost)), 0)
              .toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
          </p>
          <small style={{ color: 'var(--color-text-muted)' }}>
            {properties.filter(p => p.acquisition_cost != null && parseFloat(p.acquisition_cost) > 0).length} properties with cost data
          </small>
        </div>

        {/* Average ROI Card */}
        <div className="card" style={{ padding: '20px' }}>
          <h3 style={{ margin: '0 0 10px 0', fontSize: '16px', color: 'var(--color-text-light)' }}>📈 Average ROI</h3>
          <p style={{ margin: 0, fontSize: '28px', fontWeight: 'bold', color: 'var(--sandy-gold)' }}>
            {properties.filter(p => p.acquisition_cost != null && parseFloat(p.acquisition_cost) > 0).length > 0
              ? (properties
                  .filter(p => p.acquisition_cost != null && parseFloat(p.acquisition_cost) > 0)
                  .reduce((sum, p) => sum + (((parseFloat(p.price) - parseFloat(p.acquisition_cost)) / parseFloat(p.acquisition_cost)) * 100), 0) /
                  properties.filter(p => p.acquisition_cost != null && parseFloat(p.acquisition_cost) > 0).length
                ).toFixed(1)
              : '0.0'}%
          </p>
          <small style={{ color: 'var(--color-text-muted)' }}>Across all properties</small>
        </div>

        {/* Most Profitable Property Card */}
        <div className="card" style={{ padding: '20px' }}>
          <h3 style={{ margin: '0 0 10px 0', fontSize: '16px', color: 'var(--color-text-light)' }}>🏆 Most Profitable</h3>
          {properties.filter(p => p.acquisition_cost != null && parseFloat(p.acquisition_cost) > 0).length > 0 ? (
            <>
              <p style={{ margin: '0 0 5px 0', fontSize: '18px', fontWeight: 'bold' }}>
                ${Math.max(...properties
                  .filter(p => p.acquisition_cost != null && parseFloat(p.acquisition_cost) > 0)
                  .map(p => parseFloat(p.price) - parseFloat(p.acquisition_cost))
                ).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
              </p>
              <small style={{ color: 'var(--color-text-muted)' }}>
                {properties
                  .filter(p => p.acquisition_cost != null && parseFloat(p.acquisition_cost) > 0)
                  .sort((a, b) => (parseFloat(b.price) - parseFloat(b.acquisition_cost)) - (parseFloat(a.price) - parseFloat(a.acquisition_cost)))[0]?.title}
              </small>
            </>
          ) : (
            <p style={{ margin: 0, color: 'var(--color-text-muted)' }}>No data yet</p>
          )}
        </div>
      </div>

      {/* Add/Edit Property Form */}
      {showAddForm && <PropertyForm onSuccess={() => { setShowAddForm(false); loadProperties(); }} />}
      {editingProperty && (
        <div ref={editFormRef}>
          <PropertyForm
            property={editingProperty}
            onSuccess={() => { setEditingProperty(null); loadProperties(); }}
            onCancel={() => setEditingProperty(null)}
          />
        </div>
      )}

      {/* Desktop Properties Table */}
      <div className="desktop-only">
        <div className="card" style={{ padding: 0 }}>
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
            <thead>
              <tr style={{ backgroundColor: 'var(--light-green)', borderBottom: '2px solid var(--forest-green)' }}>
                <th style={{ padding: '15px', textAlign: 'left' }}>Property</th>
                <th style={{ padding: '15px', textAlign: 'left' }}>Location</th>
                <th style={{ padding: '15px', textAlign: 'center' }}>Price</th>
                <th style={{ padding: '15px', textAlign: 'center' }}>Acres</th>
                <th style={{ padding: '15px', textAlign: 'center' }}>Status</th>
                <th style={{ padding: '15px', textAlign: 'center' }}>Profit</th>
                <th style={{ padding: '15px', textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
          <tbody>
            {properties.map(property => (
              <tr key={property.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                <td style={{ padding: '15px' }}>
                  <strong>{property.title}</strong>
                  <br />
                  <small style={{ color: 'var(--color-text-light)' }}>ID: {property.id}</small>
                </td>
                <td style={{ padding: '15px' }}>
                  {property.county}, {property.state}
                </td>
                <td style={{ padding: '15px', textAlign: 'center' }}>
                  ${parseFloat(property.price).toLocaleString()}
                </td>
                <td style={{ padding: '15px', textAlign: 'center' }}>
                  {parseFloat(property.acres).toFixed(1)}
                </td>
                <td style={{ padding: '15px', textAlign: 'center' }}>
                  {getStatusBadge(property.status)}
                </td>
                <td style={{ padding: '15px', textAlign: 'right' }}>
                  {property.acquisition_cost != null && parseFloat(property.acquisition_cost) > 0 ? (
                    <div>
                      <span style={{
                        color: (parseFloat(property.price) - parseFloat(property.acquisition_cost)) >= 0 ? '#10b981' : '#ef4444',
                        fontWeight: 'bold',
                        fontSize: '16px'
                      }}>
                        ${(parseFloat(property.price) - parseFloat(property.acquisition_cost)).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                      </span>
                      <br />
                      <small style={{ color: 'var(--color-text-light)', fontSize: '12px' }}>
                        {(((parseFloat(property.price) - parseFloat(property.acquisition_cost)) / parseFloat(property.acquisition_cost)) * 100).toFixed(1)}% ROI
                      </small>
                    </div>
                  ) : (
                    <span style={{ color: 'var(--color-text-muted)' }}>—</span>
                  )}
                </td>
                <td style={{ padding: '15px', textAlign: 'center' }}>
                  <select
                    value={property.status}
                    onChange={(e) => !demoDisabled && updateStatus(property.id, e.target.value)}
                    disabled={demoDisabled}
                    style={{
                      padding: '5px 10px',
                      borderRadius: '4px',
                      border: '1px solid var(--color-border)',
                      fontSize: '16px',
                      minHeight: '44px',
                      boxSizing: 'border-box',
                      cursor: demoDisabled ? 'not-allowed' : 'pointer',
                      marginBottom: '5px',
                      display: 'block',
                      width: '100%',
                      opacity: demoDisabled ? 0.5 : 1
                    }}
                    title={demoDisabled ? 'Disabled in demo mode' : ''}
                  >
                    <option value="draft">Draft (not public)</option>
                    <option value="available">Available</option>
                    <option value="coming_soon">Coming Soon</option>
                    <option value="pending">Pending</option>
                    <option value="under_contract">Under Contract</option>
                    <option value="sold">Sold</option>
                  </select>
                  <button
                    onClick={() => !demoDisabled && openExpensesModal(property)}
                    disabled={demoDisabled}
                    className="btn"
                    style={{
                      padding: '5px 15px',
                      fontSize: '14px',
                      width: '100%',
                      marginBottom: '5px',
                      backgroundColor: '#f59e0b',
                      color: 'white',
                      ...demoStyle
                    }}
                    title={demoDisabled ? 'Disabled in demo mode' : ''}
                  >
                    💰 Expenses
                  </button>
		  <button
                    onClick={() => !demoDisabled && openTaxPaymentModal(property)}
                    disabled={demoDisabled}
                    className="btn"
                    style={{
                      padding: '5px 15px',
                      fontSize: '14px',
                      width: '100%',
                      marginBottom: '5px',
                      backgroundColor: '#28a745',
                      color: 'white',
                      ...demoStyle
                    }}
                    title={demoDisabled ? 'Disabled in demo mode' : ''}
                  >
                    💵 Pay Taxes
                  </button>
                  <button
                    onClick={() => !demoDisabled && openImagesModal(property)}
                    disabled={demoDisabled}
                    className="btn"
                    style={{
                      padding: '5px 15px',
                      fontSize: '14px',
                      width: '100%',
                      marginBottom: '5px',
                      backgroundColor: '#6366f1',
                      color: 'white',
                      ...demoStyle
                    }}
                    title={demoDisabled ? 'Disabled in demo mode' : ''}
                  >
                    📷 Images
                  </button>
                  <button
                    onClick={() => {
                      if (demoDisabled) return;
                      setEditingProperty(property);
                      setTimeout(() => {
                        editFormRef.current?.scrollIntoView({ behavior: 'smooth' });
                      }, 100);
                    }}
                    disabled={demoDisabled}
                    className="btn"
                    style={{
                      padding: '5px 15px',
                      fontSize: '14px',
                      width: '100%',
                      marginBottom: '5px',
                      ...demoStyle
                    }}
                    title={demoDisabled ? 'Disabled in demo mode' : ''}
                  >
                    ✏️ Edit
                  </button>
                  <button
                    onClick={() => !demoDisabled && deleteProperty(property.id, property.title)}
                    disabled={demoDisabled}
                    className="btn"
                    style={{
                      padding: '5px 15px',
                      fontSize: '14px',
                      width: '100%',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      ...demoStyle
                    }}
                    title={demoDisabled ? 'Disabled in demo mode' : ''}
                  >
                    🗑️ Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
          </div>
        </div>
      </div>

      {/* Mobile Properties Cards */}
      <div className="mobile-only">
        {properties.map(property => (
          <div key={property.id} style={{
            background: 'var(--color-surface)',
            border: '2px solid var(--color-border)',
            borderRadius: '8px',
            padding: '15px',
            marginBottom: '15px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <div style={{ marginBottom: '10px', borderBottom: '2px solid var(--color-brand-text)', paddingBottom: '10px' }}>
              <div style={{ fontWeight: 'bold', fontSize: '16px', color: 'var(--color-brand-text)' }}>
                {property.title}
              </div>
              <div style={{ fontSize: '14px', color: 'var(--color-text-light)' }}>ID: {property.id}</div>
            </div>

            <div style={{ marginBottom: '10px' }}>
              <div style={{ fontSize: '12px', color: 'var(--color-text-light)' }}>Location</div>
              <div style={{ fontWeight: '600' }}>{property.county}, {property.state}</div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
              <div>
                <div style={{ fontSize: '12px', color: 'var(--color-text-light)' }}>Price</div>
                <div style={{ fontWeight: '600', color: 'var(--color-brand-text)' }}>
                  ${parseFloat(property.price).toLocaleString()}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: 'var(--color-text-light)' }}>Acres</div>
                <div style={{ fontWeight: '600' }}>
                  {parseFloat(property.acres).toFixed(1)}
                </div>
              </div>
            </div>

            <div style={{ marginBottom: '10px' }}>
              <div style={{ fontSize: '12px', color: 'var(--color-text-light)', marginBottom: '5px' }}>Status</div>
              {getStatusBadge(property.status)}
            </div>

            {property.acquisition_cost != null && parseFloat(property.acquisition_cost) > 0 && (
              <div style={{ marginBottom: '10px', padding: '10px', backgroundColor: 'var(--light-green)', borderRadius: '5px' }}>
                <div style={{ fontSize: '12px', color: 'var(--color-text-light)' }}>Profit</div>
                <div style={{
                  color: (parseFloat(property.price) - parseFloat(property.acquisition_cost)) >= 0 ? '#10b981' : '#ef4444',
                  fontWeight: 'bold',
                  fontSize: '16px'
                }}>
                  ${(parseFloat(property.price) - parseFloat(property.acquisition_cost)).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--color-text-light)' }}>
                  {(((parseFloat(property.price) - parseFloat(property.acquisition_cost)) / parseFloat(property.acquisition_cost)) * 100).toFixed(1)}% ROI
                </div>
              </div>
            )}

            <div style={{ marginTop: '15px' }}>
              <select
                value={property.status}
                onChange={(e) => !demoDisabled && updateStatus(property.id, e.target.value)}
                disabled={demoDisabled}
                style={{
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid var(--color-border)',
                  fontSize: '16px',
                  minHeight: '44px',
                  boxSizing: 'border-box',
                  width: '100%',
                  marginBottom: '8px',
                  opacity: demoDisabled ? 0.5 : 1,
                  cursor: demoDisabled ? 'not-allowed' : 'pointer'
                }}
                title={demoDisabled ? 'Disabled in demo mode' : ''}
              >
                <option value="draft">Draft (not public)</option>
                <option value="available">Available</option>
                <option value="coming_soon">Coming Soon</option>
                <option value="pending">Pending</option>
                <option value="under_contract">Contract</option>
                <option value="sold">Sold</option>
              </select>

              <button
                onClick={() => !demoDisabled && openExpensesModal(property)}
                disabled={demoDisabled}
                className="btn"
                style={{
                  padding: '8px',
                  fontSize: '14px',
                  width: '100%',
                  marginBottom: '8px',
                  backgroundColor: '#f59e0b',
                  color: 'white',
                  ...demoStyle
                }}
                title={demoDisabled ? 'Disabled in demo mode' : ''}
              >
                💰 Expenses
              </button>

              <button
                onClick={() => !demoDisabled && openTaxPaymentModal(property)}
                disabled={demoDisabled}
                className="btn"
                style={{
                  padding: '8px',
                  fontSize: '14px',
                  width: '100%',
                  marginBottom: '8px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  ...demoStyle
                }}
                title={demoDisabled ? 'Disabled in demo mode' : ''}
              >
                💵 Pay Taxes
              </button>

              <button
                onClick={() => !demoDisabled && openImagesModal(property)}
                disabled={demoDisabled}
                className="btn"
                style={{
                  padding: '8px',
                  fontSize: '14px',
                  width: '100%',
                  marginBottom: '8px',
                  backgroundColor: '#6366f1',
                  color: 'white',
                  ...demoStyle
                }}
                title={demoDisabled ? 'Disabled in demo mode' : ''}
              >
                📷 Images
              </button>

              <button
                onClick={() => {
                  if (demoDisabled) return;
                  setEditingProperty(property);
                  setTimeout(() => {
                    editFormRef.current?.scrollIntoView({ behavior: 'smooth' });
                  }, 100);
                }}
                disabled={demoDisabled}
                className="btn"
                style={{
                  padding: '8px',
                  fontSize: '14px',
                  width: '100%',
                  marginBottom: '8px',
                  ...demoStyle
                }}
                title={demoDisabled ? 'Disabled in demo mode' : ''}
              >
                ✏️ Edit
              </button>

              <button
                onClick={() => !demoDisabled && deleteProperty(property.id, property.title)}
                disabled={demoDisabled}
                className="btn"
                style={{
                  padding: '8px',
                  fontSize: '14px',
                  width: '100%',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  ...demoStyle
                }}
                title={demoDisabled ? 'Disabled in demo mode' : ''}
              >
                🗑️ Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Expenses Modal */}
      {showExpensesModal && selectedProperty && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--color-surface)',
            padding: '30px',
            borderRadius: '10px',
            maxWidth: '800px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0 }}>💰 Selling Expenses - {selectedProperty.title}</h2>
              <button 
                onClick={() => {
                  setShowExpensesModal(false);
                  setShowAddExpenseForm(false);
                }}
                className="btn"
                style={{ padding: '8px 20px' }}
              >
                Close
              </button>
            </div>

            {/* Summary */}
            <div className="form-grid-3" style={{
              padding: '15px',
              backgroundColor: 'var(--color-success-bg)',
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <div>
                <div style={{ fontSize: '12px', color: 'var(--color-text-light)' }}>Sale Price</div>
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: 'var(--color-brand-text)' }}>
                  ${selectedProperty.price.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: 'var(--color-text-light)' }}>Total Expenses</div>
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: 'var(--color-error)' }}>
                  ${expenses.reduce((sum, exp) => sum + parseFloat(exp.amount), 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: 'var(--color-text-light)' }}>Net After Expenses</div>
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: 'var(--sandy-gold)' }}>
                  ${(parseFloat(selectedProperty.price) - parseFloat(selectedProperty.acquisition_cost || 0) - expenses.reduce((sum, exp) => sum + parseFloat(exp.amount), 0)).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                </div>
              </div>
            </div>

            {/* Add Expense Button/Form */}
            {!showAddExpenseForm ? (
              <button
                onClick={() => setShowAddExpenseForm(true)}
                className="btn btn-primary"
                style={{ marginBottom: '20px', width: '100%' }}
              >
                + Add Expense
              </button>
            ) : (
              <div style={{ 
                padding: '20px', 
                backgroundColor: 'var(--color-surface-sunken)', 
                borderRadius: '8px', 
                marginBottom: '20px',
                border: '2px solid var(--color-brand-text)'
              }}>
                <h3 style={{ marginTop: 0 }}>Add New Expense</h3>
                <form onSubmit={handleAddExpense}>
                  <div className="form-grid-2">
                    <div className="form-group">
                      <label>Date *</label>
                      <input
                        type="date"
                        value={expenseFormData.expense_date}
                        onChange={(e) => setExpenseFormData({...expenseFormData, expense_date: e.target.value})}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Category *</label>
                      <select
                        value={expenseFormData.category}
                        onChange={(e) => setExpenseFormData({...expenseFormData, category: e.target.value})}
                        required
                      >
                        <option value="Postal/Mailing">Postal/Mailing</option>
                        <option value="Deed Transfer">Deed Transfer</option>
                        <option value="Legal Fees">Legal Fees</option>
                        <option value="Marketing">Marketing</option>
                        <option value="Property Cleanup">Property Cleanup</option>
                        <option value="Travel">Travel</option>
                        <option value="Miscellaneous">Miscellaneous</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Description</label>
                    <input
                      type="text"
                      value={expenseFormData.description}
                      onChange={(e) => setExpenseFormData({...expenseFormData, description: e.target.value})}
                      placeholder="Brief description of expense"
                    />
                  </div>
                  <div className="form-group">
                    <label>Amount *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={expenseFormData.amount}
                      onChange={(e) => setExpenseFormData({...expenseFormData, amount: e.target.value})}
                      placeholder="0.00"
                      required
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>
                      Save Expense
                    </button>
                    <button 
                      type="button" 
                      onClick={() => setShowAddExpenseForm(false)}
                      className="btn"
                      style={{ flex: 1 }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Expenses List */}
            {expenses.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--color-text-light)' }}>
                No expenses recorded yet
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: 'var(--light-green)', borderBottom: '2px solid var(--forest-green)' }}>
                    <th style={{ padding: '10px', textAlign: 'left' }}>Date</th>
                    <th style={{ padding: '10px', textAlign: 'left' }}>Category</th>
                    <th style={{ padding: '10px', textAlign: 'left' }}>Description</th>
                    <th style={{ padding: '10px', textAlign: 'right' }}>Amount</th>
                    <th style={{ padding: '10px', textAlign: 'center' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {expenses.map(expense => (
                    <tr key={expense.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                      <td style={{ padding: '10px' }}>
                        {new Date(expense.expense_date).toLocaleDateString()}
                      </td>
                      <td style={{ padding: '10px' }}>{expense.category}</td>
                      <td style={{ padding: '10px' }}>{expense.description || '—'}</td>
                      <td style={{ padding: '10px', textAlign: 'right', fontWeight: 'bold' }}>
                        ${parseFloat(expense.amount).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                      </td>
                      <td style={{ padding: '10px', textAlign: 'center' }}>
                        <button
                          onClick={() => deleteExpense(expense.id)}
                          className="btn"
                          style={{
                            padding: '5px 10px',
                            fontSize: '12px',
                            backgroundColor: '#dc3545',
                            color: 'white'
                          }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Images Modal - CLOUDINARY VERSION */}
      {showImagesModal && selectedPropertyForImages && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--color-surface)',
            padding: '30px',
            borderRadius: '10px',
            maxWidth: '900px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0 }}>📷 Property Images - {selectedPropertyForImages.title}</h2>
              <button 
                onClick={() => setShowImagesModal(false)}
                className="btn"
                style={{ padding: '8px 20px' }}
              >
                Close
              </button>
            </div>

            {/* Image Count */}
            <div style={{ 
              padding: '10px 15px', 
              backgroundColor: 'var(--color-success-bg)', 
              borderRadius: '8px', 
              marginBottom: '20px',
              textAlign: 'center'
            }}>
              <strong>{images.length} / 10</strong> images uploaded
            </div>

            {/* Upload Form - Bulk Upload with Drag & Drop */}
            {images.length < 10 && (
              <div style={{ 
                padding: '20px', 
                backgroundColor: 'var(--color-surface-sunken)', 
                borderRadius: '8px', 
                marginBottom: '20px',
                border: '2px solid var(--color-brand-text)'
              }}>
                <h3 style={{ marginTop: 0 }}>📤 Upload Images</h3>
                
                {/* Drag & Drop Zone */}
                <div
                  onDragOver={(e) => { e.preventDefault(); setIsDraggingFiles(true); }}
                  onDragLeave={() => setIsDraggingFiles(false)}
                  onDrop={handleFilesDrop}
                  style={{
                    border: `3px dashed ${isDraggingFiles ? '#2563eb' : '#cbd5e1'}`,
                    borderRadius: '12px',
                    padding: '40px 20px',
                    textAlign: 'center',
                    backgroundColor: isDraggingFiles ? '#eff6ff' : 'white',
                    transition: 'all 0.2s',
                    marginBottom: '15px',
                    cursor: 'pointer'
                  }}
                  onClick={() => document.getElementById('bulk-file-input').click()}
                >
                  <div style={{ fontSize: '48px', marginBottom: '10px' }}>📤</div>
                  <p style={{ margin: '0 0 10px 0', fontSize: '16px', fontWeight: 'bold', color: 'var(--color-text)' }}>
                    Drop images here or click to select
                  </p>
                  <p style={{ margin: 0, fontSize: '13px', color: 'var(--color-text-light)' }}>
                    JPG, PNG, GIF, WebP • Max 10MB per image • Up to {10 - images.length} remaining
                  </p>
                  <input
                    id="bulk-file-input"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => handleFilesSelected(Array.from(e.target.files))}
                    style={{ display: 'none' }}
                  />
                </div>

                {/* Selected Files Preview */}
                {selectedFiles.length > 0 && (
                  <div style={{ marginBottom: '15px' }}>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center', 
                      marginBottom: '10px' 
                    }}>
                      <h4 style={{ margin: 0, fontSize: '14px', color: 'var(--color-text)' }}>
                        Selected: {selectedFiles.length} {selectedFiles.length === 1 ? 'file' : 'files'}
                      </h4>
                      <button
                        type="button"
                        onClick={() => setSelectedFiles([])}
                        className="btn"
                        style={{
                          padding: '4px 12px',
                          fontSize: '12px',
                          backgroundColor: 'var(--color-surface-sunken)',
                          color: 'var(--color-text-light)'
                        }}
                      >
                        Clear All
                      </button>
                    </div>
                    
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', 
                      gap: '10px',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      padding: '10px',
                      backgroundColor: 'var(--color-surface)',
                      borderRadius: '8px'
                    }}>
                      {selectedFiles.map((file, index) => (
                        <div 
                          key={index}
                          style={{ 
                            position: 'relative',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            border: '2px solid var(--color-border)',
                            backgroundColor: 'var(--color-surface-sunken)'
                          }}
                        >
                          <img
                            src={URL.createObjectURL(file)}
                            alt={file.name}
                            style={{
                              width: '100%',
                              height: '100px',
                              objectFit: 'cover'
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => removeSelectedFile(index)}
                            style={{
                              position: 'absolute',
                              top: '5px',
                              right: '5px',
                              backgroundColor: 'rgba(239, 68, 68, 0.9)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '50%',
                              width: '24px',
                              height: '24px',
                              cursor: 'pointer',
                              fontSize: '14px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: 0
                            }}
                          >
                            ×
                          </button>
                          {uploadProgress[index] && (
                            <div style={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              backgroundColor: 'rgba(0,0,0,0.7)',
                              color: 'white',
                              fontSize: '10px',
                              padding: '2px',
                              textAlign: 'center'
                            }}>
                              {uploadProgress[index].status === 'uploading' && 'Uploading…'}
                              {uploadProgress[index].status === 'completed' && '✓'}
                              {uploadProgress[index].status === 'error' && '✗'}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Upload Button */}
                {selectedFiles.length > 0 && (
                  <button
                    type="button"
                    onClick={uploadAllFiles}
                    className="btn btn-primary"
                    style={{ width: '100%' }}
                    disabled={uploadingImage}
                  >
                    {uploadingImage 
                      ? `⏳ Uploading ${Object.values(uploadProgress).filter(p => p.status === 'completed').length} / ${selectedFiles.length}...`
                      : `🚀 Upload ${selectedFiles.length} ${selectedFiles.length === 1 ? 'Image' : 'Images'}`
                    }
                  </button>
                )}

                {/* Legacy Single Upload Option */}
                {selectedFiles.length === 0 && (
                  <details style={{ marginTop: '15px' }}>
                    <summary style={{ 
                      cursor: 'pointer', 
                      color: 'var(--color-text-light)', 
                      fontSize: '13px',
                      userSelect: 'none'
                    }}>
                      Or upload one image at a time
                    </summary>
                    <form onSubmit={handleImageUpload} style={{ marginTop: '15px' }}>
                      <div className="form-group">
                        <label>Select Image</label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => setSelectedImageFile(e.target.files[0])}
                          required
                          style={{
                            width: '100%',
                            padding: '10px',
                            border: '1px solid var(--color-border)',
                            borderRadius: '6px'
                          }}
                        />
                      </div>
                      <div className="form-group">
                        <label>Caption (Optional)</label>
                        <input
                          type="text"
                          value={imageCaption}
                          onChange={(e) => setImageCaption(e.target.value)}
                          placeholder="Front view, Aerial shot, etc."
                        />
                      </div>
                      <button 
                        type="submit" 
                        className="btn" 
                        style={{ width: '100%' }}
                        disabled={uploadingImage}
                      >
                        {uploadingImage ? 'Uploading…' : 'Upload Single Image'}
                      </button>
                    </form>
                  </details>
                )}
              </div>
            )}

            {/* Images Grid */}
            {loadingImages ? (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--color-text-light)' }}>
                Loading images...
              </div>
            ) : images.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--color-text-light)' }}>
                No images uploaded yet. Upload your first image above!
              </div>
            ) : (
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', 
                gap: '15px' 
              }}>
                {images.map((image, index) => (
                  <div 
                    key={image.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, index)}
                    onDragEnd={handleDragEnd}
                    style={{ 
                      border: '2px solid var(--color-border)', 
                      borderRadius: '8px', 
                      overflow: 'hidden',
                      backgroundColor: 'var(--color-surface)',
                      position: 'relative',
                      cursor: draggedImageIndex === index ? 'grabbing' : 'grab',
                      opacity: draggedImageIndex === index ? 0.5 : 1,
                      transition: 'opacity 0.2s'
                    }}>
                    <div style={{
                      position: 'absolute',
                      top: '10px',
                      left: '10px',
                      backgroundColor: 'rgba(0,0,0,0.6)',
                      color: 'white',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      zIndex: 1
                    }}>
                      ☰ {index + 1}
                    </div>
                    {image.is_featured && (
                      <div style={{
                        position: 'absolute',
                        top: '10px',
                        right: '10px',
                        backgroundColor: '#f59e0b',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        zIndex: 1
                      }}>
                        ⭐ Featured
                      </div>
                    )}
                    <img 
                      src={image.url} 
                      alt={image.caption || 'Property'} 
                      style={{ 
                        width: '100%', 
                        height: '150px', 
                        objectFit: 'cover',
                        pointerEvents: 'none'
                      }}
                    />
                    <div style={{ padding: '10px' }}>
                      <div style={{ marginBottom: '10px' }}>
                        {image.caption && (
                          <p style={{ 
                            margin: '0 0 5px 0', 
                            fontSize: '13px', 
                            color: 'var(--color-text-light)' 
                          }}>
                            {image.caption}
                          </p>
                        )}
                        <button
                          onClick={() => {
                            const caption = prompt(
                              image.caption ? 'Edit caption:' : 'Enter caption for this image:', 
                              image.caption || ''
                            );
                            if (caption !== null) updateImageCaption(image.id, caption);
                          }}
                          className="btn"
                          style={{
                            padding: '5px 10px',
                            fontSize: '12px',
                            width: '100%',
                            marginBottom: '5px',
                            backgroundColor: '#6366f1',
                            color: 'white'
                          }}
                        >
                          ✏️ {image.caption ? 'Edit Caption' : 'Add Caption'}
                        </button>
                      </div>
                      <div style={{ display: 'flex', gap: '5px' }}>
                        {!image.is_featured && (
                          <button
                            onClick={() => setFeaturedImage(image.id)}
                            className="btn"
                            style={{
                              padding: '5px 10px',
                              fontSize: '12px',
                              flex: 1,
                              backgroundColor: '#f59e0b',
                              color: 'white'
                            }}
                          >
                            ⭐ Feature
                          </button>
                        )}
                        <button
                          onClick={() => deleteImage(image.id)}
                          className="btn"
                          style={{
                            padding: '5px 10px',
                            fontSize: '12px',
                            flex: 1,
                            backgroundColor: '#dc3545',
                            color: 'white'
                          }}
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tax Payment Modal */}
      {showTaxPaymentModal && selectedPropertyForTax && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--color-surface)',
            padding: '30px',
            borderRadius: '10px',
            maxWidth: '800px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0 }}>💵 Tax Payments - {selectedPropertyForTax.title}</h2>
              <button 
                onClick={() => {
                  setShowTaxPaymentModal(false);
                  setShowAddTaxPaymentForm(false);
                }}
                className="btn"
                style={{ padding: '8px 20px' }}
              >
                Close
              </button>
            </div>

            {/* Summary */}
            <div style={{ 
              padding: '15px', 
              backgroundColor: 'var(--color-success-bg)', 
              borderRadius: '8px', 
              marginBottom: '20px'
            }}>
              <div style={{ fontSize: '12px', color: 'var(--color-text-light)' }}>Annual Tax Amount</div>
              <div style={{ fontSize: '20px', fontWeight: 'bold', color: 'var(--color-brand-text)' }}>
                ${selectedPropertyForTax.annual_tax_amount ? selectedPropertyForTax.annual_tax_amount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) : '0.00'}
              </div>
              {/* What the county actually expects, and what has been remitted.
                  The admin was previously reading these off a paper bill. */}
              <TaxInstallmentLines schedule={taxSchedule} />
            </div>

            {/* Add Payment Button/Form */}
            {!showAddTaxPaymentForm ? (
              <button
                onClick={() => setShowAddTaxPaymentForm(true)}
                className="btn btn-primary"
                style={{ marginBottom: '20px', width: '100%' }}
              >
                + Record Tax Payment
              </button>
            ) : (
              <div style={{ 
                padding: '20px', 
                backgroundColor: 'var(--color-surface-sunken)', 
                borderRadius: '8px', 
                marginBottom: '20px',
                border: '2px solid var(--color-brand-text)'
              }}>
                <h3 style={{ marginTop: 0 }}>Record Tax Payment to County</h3>
                <form onSubmit={handleAddTaxPayment}>
                  <div className="form-grid-2">
                    <div className="form-group">
                      <label>Payment Date *</label>
                      <input
                        type="date"
                        value={taxPaymentFormData.payment_date}
                        onChange={(e) => setTaxPaymentFormData({...taxPaymentFormData, payment_date: e.target.value})}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Amount Paid *</label>
                      <input
                        type="number"
                        step="0.01"
                        value={taxPaymentFormData.amount}
                        onChange={(e) => setTaxPaymentFormData({...taxPaymentFormData, amount: e.target.value})}
                        placeholder="0.00"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Tax Year *</label>
                      <input
                        type="number"
                        value={taxPaymentFormData.tax_year}
                        onChange={(e) => setTaxPaymentFormData({...taxPaymentFormData, tax_year: e.target.value})}
                        required
                      />
                    </div>
                    {/* Which instalment this remittance settles. Blank means the
                        whole year, which is how a single cheque for the full bill
                        is recorded, and the settlement rule then treats both
                        instalments as covered. */}
                    <div className="form-group">
                      <label>Installment</label>
                      <select
                        value={taxPaymentFormData.instalment}
                        onChange={(e) => applyInstalmentSelection(e.target.value)}
                      >
                        <option value="">Whole year</option>
                        <option value="1">1st installment</option>
                        <option value="2">2nd installment</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Payment Method</label>
                      <select
                        value={taxPaymentFormData.payment_method}
                        onChange={(e) => setTaxPaymentFormData({...taxPaymentFormData, payment_method: e.target.value})}
                      >
                        <option value="Check">Check</option>
                        <option value="Wire Transfer">Wire Transfer</option>
                        <option value="ACH">ACH</option>
                        <option value="Cash">Cash</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Check Number</label>
                      <input
                        type="text"
                        value={taxPaymentFormData.check_number}
                        onChange={(e) => setTaxPaymentFormData({...taxPaymentFormData, check_number: e.target.value})}
                        placeholder="Optional"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Notes</label>
                    <textarea
                      rows="2"
                      value={taxPaymentFormData.notes}
                      onChange={(e) => setTaxPaymentFormData({...taxPaymentFormData, notes: e.target.value})}
                      placeholder="Additional notes about this payment"
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>
                      Save Payment
                    </button>
                    <button 
                      type="button" 
                      onClick={() => setShowAddTaxPaymentForm(false)}
                      className="btn"
                      style={{ flex: 1 }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Payment History */}
            {taxPayments.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--color-text-light)' }}>
                No tax payments recorded yet
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: 'var(--light-green)', borderBottom: '2px solid var(--forest-green)' }}>
                    <th style={{ padding: '10px', textAlign: 'left' }}>Date</th>
                    <th style={{ padding: '10px', textAlign: 'left' }}>Tax Year</th>
                    <th style={{ padding: '10px', textAlign: 'left' }}>Method</th>
                    <th style={{ padding: '10px', textAlign: 'left' }}>Check #</th>
                    <th style={{ padding: '10px', textAlign: 'right' }}>Amount</th>
                    <th style={{ padding: '10px', textAlign: 'center' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {taxPayments.map(payment => (
                    <tr key={payment.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                      <td style={{ padding: '10px' }}>
                        {new Date(payment.payment_date).toLocaleDateString()}
                      </td>
                      <td style={{ padding: '10px' }}>{payment.tax_year}</td>
                      <td style={{ padding: '10px' }}>{payment.payment_method || '—'}</td>
                      <td style={{ padding: '10px' }}>{payment.check_number || '—'}</td>
                      <td style={{ padding: '10px', textAlign: 'right', fontWeight: 'bold' }}>
                        ${parseFloat(payment.amount).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                      </td>
                      <td style={{ padding: '10px', textAlign: 'center' }}>
                        <button
                          onClick={() => deleteTaxPayment(payment.id)}
                          className="btn"
                          style={{
                            padding: '5px 10px',
                            fontSize: '12px',
                            backgroundColor: '#dc3545',
                            color: 'white'
                          }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
// Property Form Component (Add/Edit)
function PropertyForm({ property, onSuccess, onCancel }) {
  const isEditing = !!property;
  const navigate = useNavigate();
  const [availableStates, setAvailableStates] = useState([]);
  
  useEffect(() => {
    const loadStates = async () => {
      try {
        const token = localStorage.getItem('adminToken');
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/admin/states`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setAvailableStates(response.data);
      } catch (err) {
        console.error('Failed to load states:', err);
      }
    };
    loadStates();
  }, []);
  
  const parseCoordinates = (coordString) => {
    if (!coordString) return { ne: '', se: '', sw: '', nw: '', center: '' };
    try {
      const coords = JSON.parse(coordString);
      return {
        ne: coords.ne_corner || '',
        se: coords.se_corner || '',
        sw: coords.sw_corner || '',
        nw: coords.nw_corner || '',
        center: coords.center || ''
      };
    } catch {
      return { ne: '', se: '', sw: '', nw: '', center: '' };
    }
  };
  const existingCoords = isEditing ? parseCoordinates(property.coordinates) : {};

  const [formData, setFormData] = useState({
    title: property?.title || '',
    description: property?.description || '',
    location: property?.location || '',
    state: property?.state || '',
    county: property?.county || '',
    acres: property?.acres || '',
    price: property?.price || '',
    // ?? not ||: a recorded acquisition_cost of 0 is a fact, and || would load it
    // into the form as blank, which now means "not recorded" and would erase it
    // on the next save.
    acquisition_cost: property?.acquisition_cost ?? '',
    apn: property?.apn || '',
    legal_description: property?.legal_description || '',
    coord_ne: existingCoords.ne || '',
    coord_se: existingCoords.se || '',
    coord_sw: existingCoords.sw || '',
    coord_nw: existingCoords.nw || '',
    coord_center: existingCoords.center || '',
    annual_tax_amount: property?.annual_tax_amount || '',
    tax_payment_1_date: property?.tax_payment_1_date ? property.tax_payment_1_date.split('T')[0] : '',
    tax_payment_1_amount: property?.tax_payment_1_amount || '',
    tax_payment_2_date: property?.tax_payment_2_date ? property.tax_payment_2_date.split('T')[0] : '',
    tax_payment_2_amount: property?.tax_payment_2_amount || '',
    tax_notes: property?.tax_notes || '',
    monthly_hoa_fee: property?.monthly_hoa_fee || '',
    hoa_name: property?.hoa_name || '',
    hoa_contact: property?.hoa_contact || '',
    hoa_notes: property?.hoa_notes || '',
    property_covenants: property?.property_covenants || '',
    // Land Characteristics
    elevation: property?.elevation || '',
    terrain: property?.terrain || '',
    zoning: property?.zoning || '',
    flood_zone: property?.flood_zone || '',
    road_access: property?.road_access || '',
    // Utilities & Services
    utilities_electric: property?.utilities_electric || '',
    utilities_water: property?.utilities_water || '',
    utilities_sewer: property?.utilities_sewer || '',
    utilities_internet: property?.utilities_internet || '',
    cell_service: property?.cell_service || '',
    nearest_town: property?.nearest_town || '',
    distance_to_town: property?.distance_to_town || '',
    // Round-trips on edit, so saving a property never silently resets how its
    // processing fee is charged.
    fee_handling: property?.fee_handling || 'upfront'
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const token = localStorage.getItem('adminToken');
      
      const coordinates = JSON.stringify({
        ne_corner: formData.coord_ne || null,
        se_corner: formData.coord_se || null,
        sw_corner: formData.coord_sw || null,
        nw_corner: formData.coord_nw || null,
        center: formData.coord_center || null
      });

      const { coord_ne, coord_se, coord_sw, coord_nw, coord_center, ...submitData } = formData;
      submitData.coordinates = coordinates;

      if (isEditing) {
        submitData.status = property.status;
        await axios.put(
          `${process.env.REACT_APP_API_URL}/admin/properties/${property.id}`,
          submitData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        alert('Property updated successfully!');
      } else {
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/admin/properties`,
          submitData,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        // The server saves a new listing as a draft rather than refusing it when
        // financing is not configured yet. Say so plainly -- a silent downgrade
        // would leave the admin believing the property is live.
        const notice = response.data?.notice;
        if (notice) {
          if (window.confirm(`Property saved as a DRAFT.\n\n${notice.error}\n\nOpen ${notice.fixLabel} now?`)) {
            navigate(notice.fixPath);
            return;
          }
        } else {
          alert('Property added successfully!');
        }
      }

      onSuccess();
    } catch (err) {
      const data = err.response?.data;
      if (data?.code === 'FINANCING_TIERS_NOT_CONFIGURED') {
        setError(`${data.error}`);
      } else {
        setError(data?.error || `Failed to ${isEditing ? 'update' : 'add'} property`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card" style={{ padding: '30px', marginBottom: '30px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0 }}>{isEditing ? 'Edit Property' : 'Add New Property'}</h2>
        {isEditing && (
          <button onClick={onCancel} className="btn" style={{ padding: '8px 20px' }}>
            Cancel
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
          <div className="form-group">
            <label>Property Title *</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Location *</label>
            <input
              type="text"
              name="location"
              value={formData.location}
              onChange={handleChange}
              placeholder="e.g., Near Appleton"
              required
            />
          </div>

          <div className="form-group">
            <label>State *</label>
            <select
              name="state"
              value={formData.state}
              onChange={handleChange}
              required
            >
              <option value="">-- Select State --</option>
              {availableStates.map(state => (
                <option key={state.id} value={state.name}>
                  {state.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>County *</label>
            <input
              type="text"
              name="county"
              value={formData.county}
              onChange={handleChange}
              placeholder="e.g., Outagamie"
              required
            />
          </div>

          <div className="form-group">
            <label>Acres *</label>
            <input
              type="number"
              step="0.01"
              name="acres"
              value={formData.acres}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Price *</label>
            <input
              type="number"
              step="0.01"
              name="price"
              value={formData.price}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Processing Fee Handling</label>
            <select
              name="fee_handling"
              value={formData.fee_handling}
              onChange={handleChange}
            >
              <option value="upfront">Collect at signing</option>
              <option value="financed">Finance into the loan</option>
            </select>
            <small style={{ display: 'block', color: 'var(--color-text-light)', marginTop: '4px' }}>
              Collected at signing, the fee is added to the amount due today. Financed, it is added to the loan and paid off monthly. It is charged once either way, and this governs new loans only.
            </small>
          </div>

          <div className="form-group">
            <label>Acquisition Cost</label>
            <input
              type="number"
              step="0.01"
              min="0"
              placeholder="What you paid for this property"
              value={formData.acquisition_cost ?? ''}
              onChange={(e) => setFormData({...formData, acquisition_cost: e.target.value})}
            />
            <small style={{ color: 'var(--color-text-light)', fontSize: '12px' }}>
              Leave blank if unknown &mdash; profit and ROI will show as not recorded rather than being estimated.
            </small>
          </div>

          <div className="form-group">
            <label>APN (Optional)</label>
            <input
              type="text"
              name="apn"
              value={formData.apn}
              onChange={handleChange}
              placeholder="Assessor's Parcel Number"
            />
          </div>
        </div>

        <div className="form-group">
          <label>Legal Description</label>
          <textarea
            name="legal_description"
            value={formData.legal_description}
            onChange={handleChange}
            rows="3"
            placeholder="Legal property description (e.g., Lot 5, Block 3, County Subdivision)"
          />
          <small style={{ color: 'var(--color-text-light)', fontSize: '12px' }}>
            This will appear on the contract instead of the title
          </small>
        </div>

        {/* GPS Coordinates */}
        <div style={{ marginTop: '30px', paddingTop: '20px', borderTop: '2px solid var(--color-border)' }}>
          <label style={{ marginBottom: '15px', display: 'block', fontWeight: 'bold', fontSize: '16px' }}>
            GPS Coordinates (5-Point System - Optional)
          </label>

          {/* Row 1: NE and SE Corners */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '15px' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600', color: 'var(--color-text)' }}>
                NE Corner (Northeast)
              </label>
              <input
                type="text"
                name="coord_ne"
                value={formData.coord_ne}
                onChange={handleChange}
                placeholder="44.2619, -88.4154"
                style={{ width: '100%' }}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600', color: 'var(--color-text)' }}>
                SE Corner (Southeast)
              </label>
              <input
                type="text"
                name="coord_se"
                value={formData.coord_se}
                onChange={handleChange}
                placeholder="44.2600, -88.4154"
                style={{ width: '100%' }}
              />
            </div>
          </div>

          {/* Row 2: SW and NW Corners */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '15px' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600', color: 'var(--color-text)' }}>
                SW Corner (Southwest)
              </label>
              <input
                type="text"
                name="coord_sw"
                value={formData.coord_sw}
                onChange={handleChange}
                placeholder="44.2600, -88.4170"
                style={{ width: '100%' }}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600', color: 'var(--color-text)' }}>
                NW Corner (Northwest)
              </label>
              <input
                type="text"
                name="coord_nw"
                value={formData.coord_nw}
                onChange={handleChange}
                placeholder="44.2619, -88.4170"
                style={{ width: '100%' }}
              />
            </div>
          </div>

          {/* Row 3: Center Point */}
          <div style={{ maxWidth: 'calc(50% - 10px)' }} className="gps-center-field">
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600', color: 'var(--color-text)' }}>
                Center Point
              </label>
              <input
                type="text"
                name="coord_center"
                value={formData.coord_center}
                onChange={handleChange}
                placeholder="44.2610, -88.4162"
                style={{ width: '100%' }}
              />
            </div>
          </div>

          <small style={{ color: 'var(--color-text-light)', fontSize: '12px', marginTop: '10px', display: 'block' }}>
            Format: latitude, longitude (e.g., 44.2619, -88.4154)
          </small>
        </div>

        <div className="form-group" style={{ marginTop: '30px' }}>
          <label>Description *</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows="4"
            required
          />
        </div>

        <div className="form-group" style={{ marginTop: '20px' }}>
          <label>Property Covenants/Restrictions</label>
          <textarea
            name="property_covenants"
            value={formData.property_covenants}
            onChange={handleChange}
            rows="4"
            placeholder="Enter any covenants, restrictions, or HOA rules (optional). If none, leave blank and contract will show 'None'."
          />
          <small style={{ color: 'var(--color-text-light)', fontSize: '12px', display: 'block', marginTop: '5px' }}>
            This will be inserted into Section 27 of the Contract for Deed
          </small>
        </div>

        {/* Property Tax Section */}
        <div style={{ marginTop: '30px', paddingTop: '20px', borderTop: '2px solid var(--color-border)' }}>
          <h3 style={{ color: 'var(--color-brand-text)', marginBottom: '15px' }}>Property Tax Information</h3>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
            <div className="form-group">
              <label>Annual Tax Amount</label>
              <input
                type="number"
                step="0.01"
                placeholder="Total annual property tax"
                value={formData.annual_tax_amount || ''}
                onChange={(e) => setFormData({...formData, annual_tax_amount: e.target.value})}
              />
              <small style={{ color: 'var(--color-text-light)', fontSize: '12px', display: 'block', marginTop: '5px' }}>
                Applies to NEW loans only. Each loan's monthly tax escrow is fixed
                when the loan is created, so changing this does not alter what an
                existing buyer is charged.
              </small>
              {/* THE TWO INSTALMENTS AGAINST THE ANNUAL BILL.
                  The monthly escrow is derived from the annual figure, while the
                  instalments are what actually has to be remitted to the county.
                  When they disagree the loan collects at one rate and owes at
                  another, and until now nothing compared them.

                  NON-BLOCKING on purpose. A variance is often legitimate (a
                  supplemental assessment, a mid-year re-bill), and refusing the
                  save would stop an operator recording what the county actually
                  sent. Compared in whole cents so 56.70 + 56.70 does not report
                  a variance against 113.40. */}
              {(() => {
                const annual = parseFloat(formData.annual_tax_amount);
                const first = parseFloat(formData.tax_payment_1_amount);
                const second = parseFloat(formData.tax_payment_2_amount);
                const hasAnnual = Number.isFinite(annual) && annual > 0;
                const hasAny = Number.isFinite(first) || Number.isFinite(second);
                if (!hasAnnual || !hasAny) return null;
                const scheduled = (Number.isFinite(first) ? Math.round(first * 100) : 0)
                  + (Number.isFinite(second) ? Math.round(second * 100) : 0);
                const variance = (scheduled - Math.round(annual * 100)) / 100;
                if (variance === 0) return null;
                return (
                  <div style={{
                    marginTop: '8px', padding: '10px', borderRadius: '5px',
                    backgroundColor: 'var(--color-warning-bg)', color: 'var(--color-warning-text)', fontSize: '12px'
                  }}>
                    The two installments add up to ${(scheduled / 100).toFixed(2)}, which is
                    ${Math.abs(variance).toFixed(2)} {variance > 0 ? 'more' : 'less'} than the
                    annual tax amount of ${annual.toFixed(2)}. You can still save this.
                  </div>
                );
              })()}
            </div>
            <div></div>

            <div className="form-group">
              <label>1st Payment Due Date</label>
              <input
                type="date"
                value={formData.tax_payment_1_date || ''}
                onChange={(e) => setFormData({...formData, tax_payment_1_date: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>1st Payment Amount</label>
              <input
                type="number"
                step="0.01"
                placeholder="Amount"
                value={formData.tax_payment_1_amount || ''}
                onChange={(e) => setFormData({...formData, tax_payment_1_amount: e.target.value})}
              />
            </div>

            <div className="form-group">
              <label>2nd Payment Due Date</label>
              <input
                type="date"
                value={formData.tax_payment_2_date || ''}
                onChange={(e) => setFormData({...formData, tax_payment_2_date: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>2nd Payment Amount</label>
              <input
                type="number"
                step="0.01"
                placeholder="Amount"
                value={formData.tax_payment_2_amount || ''}
                onChange={(e) => setFormData({...formData, tax_payment_2_amount: e.target.value})}
              />
            </div>
          </div>

          <div className="form-group" style={{ marginTop: '20px' }}>
            <label>Tax Notes</label>
            <textarea
              rows="2"
              placeholder="Additional tax information, payment instructions, etc."
              value={formData.tax_notes || ''}
              onChange={(e) => setFormData({...formData, tax_notes: e.target.value})}
            />
          </div>
        </div>

        {/* HOA Section */}
        <div style={{ marginTop: '30px', paddingTop: '20px', borderTop: '2px solid var(--color-border)' }}>
          <h3 style={{ color: 'var(--color-brand-text)', marginBottom: '15px' }}>HOA Information (If Applicable)</h3>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
            <div className="form-group">
              <label>Monthly HOA Fee</label>
              <input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={formData.monthly_hoa_fee || ''}
                onChange={(e) => setFormData({...formData, monthly_hoa_fee: e.target.value})}
              />
              <small style={{ color: 'var(--color-text-light)', fontSize: '12px', display: 'block', marginTop: '5px' }}>
                Leave blank if no HOA. Applies to NEW loans only &mdash; each loan's
                monthly HOA escrow is fixed when the loan is created, so changing
                this does not alter what an existing buyer is charged.
              </small>
            </div>
            <div className="form-group">
              <label>HOA Name</label>
              <input
                type="text"
                placeholder="Name of HOA"
                value={formData.hoa_name || ''}
                onChange={(e) => setFormData({...formData, hoa_name: e.target.value})}
              />
            </div>
          </div>

          <div className="form-group">
            <label>HOA Contact</label>
            <input
              type="text"
              placeholder="Contact email or phone"
              value={formData.hoa_contact || ''}
              onChange={(e) => setFormData({...formData, hoa_contact: e.target.value})}
            />
          </div>

          <div className="form-group">
            <label>HOA Notes</label>
            <textarea
              rows="2"
              placeholder="Payment schedule, included services, etc."
              value={formData.hoa_notes || ''}
              onChange={(e) => setFormData({...formData, hoa_notes: e.target.value})}
            />
          </div>
        </div>

        {/* Land Characteristics Section */}
        <div style={{ marginTop: '30px', paddingTop: '20px', borderTop: '2px solid var(--color-border)' }}>
          <h3 style={{ color: 'var(--color-brand-text)', marginBottom: '15px' }}>Land Characteristics</h3>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
            <div className="form-group">
              <label>Elevation</label>
              <input
                type="text"
                name="elevation"
                placeholder="e.g., 1,200 ft"
                value={formData.elevation || ''}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Terrain</label>
              <select
                name="terrain"
                value={formData.terrain || ''}
                onChange={handleChange}
              >
                <option value="">-- Select --</option>
                <option value="Flat">Flat</option>
                <option value="Rolling">Rolling</option>
                <option value="Hilly">Hilly</option>
                <option value="Wooded">Wooded</option>
                <option value="Desert">Desert</option>
                <option value="Mixed">Mixed</option>
              </select>
            </div>
            <div className="form-group">
              <label>Zoning</label>
              <select
                name="zoning"
                value={formData.zoning || ''}
                onChange={handleChange}
              >
                <option value="">-- Select --</option>
                <option value="Residential">Residential</option>
                <option value="Agricultural">Agricultural</option>
                <option value="Commercial">Commercial</option>
                <option value="Industrial">Industrial</option>
                <option value="Recreational">Recreational</option>
                <option value="Unzoned">Unzoned</option>
              </select>
            </div>
            <div className="form-group">
              <label>Flood Zone</label>
              <select
                name="flood_zone"
                value={formData.flood_zone || ''}
                onChange={handleChange}
              >
                <option value="">-- Select --</option>
                <option value="None">None</option>
                <option value="Zone A">Zone A</option>
                <option value="Zone AE">Zone AE</option>
                <option value="Zone X">Zone X</option>
                <option value="Zone V">Zone V</option>
                <option value="Unknown">Unknown</option>
              </select>
            </div>
            <div className="form-group">
              <label>Road Access</label>
              <select
                name="road_access"
                value={formData.road_access || ''}
                onChange={handleChange}
              >
                <option value="">-- Select --</option>
                <option value="Paved Road">Paved Road</option>
                <option value="Gravel Road">Gravel Road</option>
                <option value="Dirt Road">Dirt Road</option>
                <option value="Easement Only">Easement Only</option>
                <option value="No Access">No Access</option>
              </select>
            </div>
          </div>
        </div>

        {/* Utilities & Services Section */}
        <div style={{ marginTop: '30px', paddingTop: '20px', borderTop: '2px solid var(--color-border)' }}>
          <h3 style={{ color: 'var(--color-brand-text)', marginBottom: '15px' }}>Utilities & Services</h3>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
            <div className="form-group">
              <label>Electric</label>
              <select
                name="utilities_electric"
                value={formData.utilities_electric || ''}
                onChange={handleChange}
              >
                <option value="">-- Select --</option>
                <option value="On-site">On-site</option>
                <option value="Available Nearby">Available Nearby</option>
                <option value="Not Available">Not Available</option>
              </select>
            </div>
            <div className="form-group">
              <label>Water</label>
              <select
                name="utilities_water"
                value={formData.utilities_water || ''}
                onChange={handleChange}
              >
                <option value="">-- Select --</option>
                <option value="Municipal">Municipal</option>
                <option value="Well On-site">Well On-site</option>
                <option value="Well Needed">Well Needed</option>
                <option value="Not Available">Not Available</option>
              </select>
            </div>
            <div className="form-group">
              <label>Sewer</label>
              <select
                name="utilities_sewer"
                value={formData.utilities_sewer || ''}
                onChange={handleChange}
              >
                <option value="">-- Select --</option>
                <option value="Municipal">Municipal</option>
                <option value="Septic On-site">Septic On-site</option>
                <option value="Septic Needed">Septic Needed</option>
                <option value="Not Available">Not Available</option>
              </select>
            </div>
            <div className="form-group">
              <label>Internet</label>
              <select
                name="utilities_internet"
                value={formData.utilities_internet || ''}
                onChange={handleChange}
              >
                <option value="">-- Select --</option>
                <option value="Fiber">Fiber</option>
                <option value="Cable">Cable</option>
                <option value="DSL">DSL</option>
                <option value="Satellite Only">Satellite Only</option>
                <option value="None">None</option>
              </select>
            </div>
            <div className="form-group">
              <label>Cell Service</label>
              <select
                name="cell_service"
                value={formData.cell_service || ''}
                onChange={handleChange}
              >
                <option value="">-- Select --</option>
                <option value="Excellent">Excellent</option>
                <option value="Good">Good</option>
                <option value="Limited">Limited</option>
                <option value="None">None</option>
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginTop: '15px' }}>
            <div className="form-group">
              <label>Nearest Town</label>
              <input
                type="text"
                name="nearest_town"
                placeholder="e.g., Appleton"
                value={formData.nearest_town || ''}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Distance to Town</label>
              <input
                type="text"
                name="distance_to_town"
                placeholder="e.g., 5 miles"
                value={formData.distance_to_town || ''}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>

        {error && (
          <div style={{
            padding: '15px',
            backgroundColor: 'var(--color-error-bg)',
            color: 'var(--color-error-text)',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            {error}
          </div>
        )}

        <button
          type="submit"
          className="btn btn-primary"
          disabled={loading}
        >
          {loading ? (isEditing ? 'Updating...' : 'Adding...') : (isEditing ? 'Update Property' : 'Add Property')}
        </button>
      </form>
    </div>
  );
}

export default PropertyManagement;